import React from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Container,
  SimpleGrid,
  Icon,
  useColorModeValue,
  useBreakpointValue
} from '@chakra-ui/react';
import { FiZap, FiShield, FiTrendingUp, FiUsers, FiSmartphone, FiHeadphones } from 'react-icons/fi';

const WhyPiePOS = () => {
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const accentColor = useColorModeValue('yellow.400', 'yellow.300');
  const cardBgColor = useColorModeValue('gray.50', 'gray.500');
  const cardHoverBgColor = useColorModeValue('white', 'gray.600');

  const headingSize = useBreakpointValue({ base: 'xl', md: '2xl' });
  const subHeadingSize = useBreakpointValue({ base: 'md', md: 'lg' });

  const features = [
    {
      icon: FiZap,
      title: 'AI First',
      description: 'Uses AI to generate and manage menus, cut down menu creation effort by over 50%.',
      color: 'yellow.400',
      bgColor: 'yellow.50'
    },
    {
      icon: FiTrendingUp,
      title: 'Custom Domain',
      description: 'Create a unique online presence with a custom domain for your restaurant\'s digital storefront.',
      color: 'red.400',
      bgColor: 'red.50'
    },
    {
      icon: FiShield,
      title: 'Secure & Reliable',
      description: '99.995% uptime. Everytime. All the time.',
      color: 'orange.400',
      bgColor: 'orange.50'
    },

    {
      icon: FiUsers,
      title: 'Campaign Management',
      description: 'Valentines day offer? Diwali special menu? Done! Run targeted campaigns via WhatsApp to engage customers and boost sales.',
      color: 'yellow.500',
      bgColor: 'yellow.50'
    },
    {
      icon: FiSmartphone,
      title: 'Mobile First',
      description: 'Fully optimized for mobile, so you can manage your business anytime, anywhere.',
      color: 'orange.500',
      bgColor: 'orange.50'
    },
    {
      icon: FiHeadphones,
      title: '24/7 Support',
      description: 'Get round-the-clock assistance from our dedicated support team. Human support to actually understand and solve your issues on priority, no AI here.',
      color: 'red.500',
      bgColor: 'red.50'
    }
  ];

  return (
    <Box minH="100vh" position="relative" overflow="hidden" marginBottom="16">
      <Box
        display={{ base: 'none', lg: 'block' }}
        position="absolute"
        top="55%"
        right="28%"
        w="140px"
        h="140px"
        bg="orange.300"
        borderRadius="40% 60% 30% 70%"
        opacity="0.5"
        zIndex="0"
        animation="pulse 5s ease-in-out infinite"
        sx={{
          '@keyframes pulse': {
            '0%, 100%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.1)' }
          }
        }}
      />

      <Box
        display={{ base: 'none', md: 'block' }}
        position="absolute"
        bottom="20%"
        left="10%"
        w="120px"
        h="120px"
        bg="red.300"
        borderRadius="60% 40% 80% 20%"
        opacity="0.7"
        zIndex="0"
        animation="float 6s ease-in-out infinite reverse"
        sx={{
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-15px) rotate(-8deg)' }
          }
        }}
      />

      <Box
        display={{ base: 'none', lg: 'block' }}
        position="absolute"
        bottom="15%"
        right="12%"
        w="100px"
        h="100px"
        bg="yellow.200"
        borderRadius="70% 30% 60% 40%"
        opacity="0.6"
        zIndex="0"
        animation="pulse 4s ease-in-out infinite"
        sx={{
          '@keyframes pulse': {
            '0%, 100%': { transform: 'scale(1) rotate(0deg)' },
            '50%': { transform: 'scale(1.05) rotate(5deg)' }
          }
        }}
      />

      {/* Decorative Lines */}
      <Box
        display={{ base: 'none', md: 'block' }}
        position="absolute"
        top="30%"
        right="0"
        w="200px"
        h="4px"
        bg={accentColor}
        borderRadius="full"
        transform="rotate(45deg)"
        opacity="0.7"
        zIndex="0"
      />

      <Box
        display={{ base: 'none', lg: 'block' }}
        position="absolute"
        top="60%"
        left="0"
        w="150px"
        h="3px"
        bg="orange.400"
        borderRadius="full"
        transform="rotate(-30deg)"
        opacity="0.6"
        zIndex="0"
      />

      {/* Mobile decorative elements */}
      <Box
        display={{ base: 'block', md: 'none' }}
        position="absolute"
        top="5%"
        right="10%"
        w="50px"
        h="50px"
        bg={accentColor}
        borderRadius="50%"
        opacity="0.6"
        zIndex="0"
        animation="pulse 3s ease-in-out infinite"
        sx={{
          '@keyframes pulse': {
            '0%, 100%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.1)' }
          }
        }}
      />

      <Box
        display={{ base: 'block', md: 'none' }}
        position="absolute"
        bottom="10%"
        left="8%"
        w="35px"
        h="35px"
        bg="orange.300"
        borderRadius="50% 50% 0 50%"
        opacity="0.7"
        zIndex="0"
        animation="float 4s ease-in-out infinite"
        sx={{
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-8px)' }
          }
        }}
      />


      <Container
        maxW="7xl"
        position="relative"
        zIndex="1"
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        py={{ base: 10, md: 0 }}
      >
        <VStack spacing={{ base: 8, md: 16 }} textAlign="center" px={{ base: 4, md: 0 }}>
          <VStack spacing={{ base: 4, md: 6 }} maxW="4xl">
            <Heading
              as="h2"
              size={headingSize}
              color={textColor}
              fontWeight="bold"
              lineHeight="1.2"
            >
              Why Choose PiePOS?
            </Heading>

            <Box position="relative">
              <Text
                fontSize={subHeadingSize}
                color="gray.600"
                maxW="3xl"
                fontWeight="medium"
                lineHeight="1.6"
              >
                <Box as="span" position="relative" display="inline-block">
                  PiePOS does what Petpooja & Zomato doesn't
                  {/* Doodle underline */}
                  <Box
                    position="absolute"
                    bottom={-1}
                    left="0"
                    w="100%"
                    h="3px"
                    bg={accentColor}
                    borderRadius="full"
                    transform="rotate(-1deg)"
                    opacity={0.8}
                  />
                </Box>
              </Text>
            </Box>
          </VStack>

          <SimpleGrid
            columns={{ base: 1, md: 2, lg: 3 }}
            spacing={{ base: 6, md: 8 }}
            w="full"
          >
            {features.map((feature, index) => (
              <Box
                key={index}
                bg={cardBgColor}
                p={{ base: 6, md: 8 }}
                borderRadius="2xl"
                border="2px solid"
                borderColor="gray.100"
                _hover={{
                  transform: 'translateY(-8px)',
                  boxShadow: 'xl',
                  bg: cardHoverBgColor,
                  borderColor: feature.color
                }}
                transition="all 0.4s ease"
                height="full"
                position="relative"
                overflow="hidden"
              >
                {/* Card decorative element */}
                <Box
                  position="absolute"
                  top="-20px"
                  right="-20px"
                  w="80px"
                  h="80px"
                  bg={feature.bgColor}
                  borderRadius="50%"
                  opacity="0.3"
                  zIndex="0"
                />

                <VStack spacing={4} height="full" position="relative" zIndex="1">
                  <Box
                    p={3}
                    bg={feature.bgColor}
                    borderRadius="2xl"
                    border="2px solid"
                    borderColor={feature.color}
                  >
                    <Icon
                      as={feature.icon}
                      boxSize="8"
                      color={feature.color}
                    />
                  </Box>

                  <Heading
                    as="h3"
                    size="md"
                    color={textColor}
                    fontWeight="bold"
                  >
                    {feature.title}
                  </Heading>

                  <Text
                    fontSize="sm"
                    color="gray.600"
                    textAlign="center"
                    flex="1"
                    lineHeight="1.6"
                    fontWeight="medium"
                  >
                    {feature.description}
                  </Text>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
};

export default WhyPiePOS;